import Cookies from 'js-cookie';
import { Channel } from '../types/Channel';

const CHANNELS_COOKIE_KEY = 'streaming_channels';

export const getFaviconUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=128`;
  } catch (error) {
    // If URL is invalid, try to extract domain anyway
    const domain = url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  }
};

export const saveChannels = (channels: Channel[]): void => {
  try {
    const channelsData = JSON.stringify(channels);
    Cookies.set(CHANNELS_COOKIE_KEY, channelsData, { expires: 365 });
    console.log('Channels saved successfully:', channels.length, 'channels');
  } catch (error) {
    console.error('Error saving channels to cookies:', error);
  }
};

export const loadChannels = (): Channel[] => {
  const channelsData = Cookies.get(CHANNELS_COOKIE_KEY);
  const defaultChannels = getDefaultChannels();
  
  if (!channelsData) {
    return defaultChannels;
  }
  
  try {
    const loadedChannels = JSON.parse(channelsData);
    
    // Check if we need to update to latest default channels
    const hasOldDefaults = loadedChannels.length < defaultChannels.length;
    const hasOnlyDefaultChannels = loadedChannels.every((channel: Channel) => 
      defaultChannels.some(defaultChannel => defaultChannel.id === channel.id)
    );
    
    // If user only has default channels and we have more defaults now, update them
    if (hasOldDefaults && hasOnlyDefaultChannels) {
      console.log('Updating to latest default channels');
      saveChannels(defaultChannels);
      return defaultChannels;
    }
    
    // Update channels to use favicon URLs if they don't have them or are using old services
    const updatedChannels = loadedChannels.map((channel: Channel) => {
      // Check if channel has no icon or is using old services (clearbit, etc.)
      const needsUpdate = !channel.iconUrl || 
                         channel.iconUrl.includes('clearbit.com') ||
                         channel.iconUrl.includes('logo.clearbit.com') ||
                         !channel.iconUrl.includes('favicon');
      
      if (needsUpdate && channel.link) {
        return {
          ...channel,
          iconUrl: getFaviconUrl(channel.link)
        };
      }
      return channel;
    });
    
    // Save updated channels back to cookies if any were updated
    const hasUpdates = updatedChannels.some((channel: Channel, index: number) => 
      channel.iconUrl !== loadedChannels[index].iconUrl
    );
    
    if (hasUpdates) {
      console.log('Updated channels with favicon URLs');
      saveChannels(updatedChannels);
    }
    
    return updatedChannels;
  } catch (error) {
    console.error('Error parsing channels from cookies:', error);
    return getDefaultChannels();
  }
};

export const getDefaultChannels = (): Channel[] => [
  {
    id: '1',
    displayName: 'Netflix',
    link: 'https://netflix.com',
    iconUrl: 'https://www.google.com/s2/favicons?domain=netflix.com&sz=128'
  },
  {
    id: '2',
    displayName: 'HBO Max',
    link: 'https://hbomax.com',
    iconUrl: 'https://www.google.com/s2/favicons?domain=hbomax.com&sz=128'
  },
  {
    id: '3',
    displayName: 'Disney+',
    link: 'https://disneyplus.com',
    iconUrl: 'https://www.google.com/s2/favicons?domain=disneyplus.com&sz=128'
  },
  {
    id: '4',
    displayName: 'Amazon Prime',
    link: 'https://primevideo.com',
    iconUrl: 'https://www.google.com/s2/favicons?domain=primevideo.com&sz=128'
  },
  {
    id: '5',
    displayName: 'SSport+',
    link: 'https://ssportplus.com',
    iconUrl: 'https://www.google.com/s2/favicons?domain=ssportplus.com&sz=128'
  },
  {
    id: '6',
    displayName: 'TV+',
    link: 'https://tvplus.com.tr',
    iconUrl: 'https://www.google.com/s2/favicons?domain=tvplus.com.tr&sz=128'
  },
  {
    id: '7',
    displayName: 'TOD',
    link: 'https://todtv.com.tr',
    iconUrl: 'https://www.google.com/s2/favicons?domain=todtv.com.tr&sz=128'
  },
  {
    id: '8',
    displayName: 'Tabii',
    link: 'https://www.tabii.com',
    iconUrl: 'https://www.google.com/s2/favicons?domain=tabii.com&sz=128'
  },
  {
    id: '9',
    displayName: 'PLEX',
    link: 'https://plex.evinrude.org',
    iconUrl: 'https://www.google.com/s2/favicons?domain=plex.tv&sz=128'
  },
  {
    id: '10',
    displayName: 'My Home',
    link: 'https://home.google.com/home',
    iconUrl: 'https://www.google.com/s2/favicons?domain=home.google.com&sz=128'
  },
  {
    id: '11',
    displayName: 'Power App',
    link: 'https://www.powerapp.com.tr/cafe-club-on-air/cafe-bar/',
    iconUrl: 'https://www.google.com/s2/favicons?domain=powerapp.com.tr&sz=128'
  },
  {
    id: '12',
    displayName: 'Home Assistant',
    link: 'https://homeassistant.evinrude.org',
    iconUrl: 'https://www.google.com/s2/favicons?domain=www.home-assistant.io&sz=128'
  }
];

export const generateChannelId = (): string => {
  return Date.now().toString() + Math.random().toString(36).substr(2, 9);
};

export const getYouTubeRedirectUrl = (channelLink: string): string => {
  return `https://www.youtube.com/redirect?q=${encodeURIComponent(channelLink)}`;
};

export const resetToDefaults = (): Channel[] => {
  try {
    Cookies.remove(CHANNELS_COOKIE_KEY);
    console.log('Channels reset to defaults');
    return getDefaultChannels();
  } catch (error) {
    console.error('Error resetting channels:', error);
    return getDefaultChannels();
  }
};

export const getStorageInfo = (): { hasData: boolean; channelCount: number; lastSaved?: string } => {
  const channelsData = Cookies.get(CHANNELS_COOKIE_KEY);
  if (!channelsData) {
    return { hasData: false, channelCount: 0 };
  }
  
  try {
    const channels = JSON.parse(channelsData);
    return {
      hasData: true,
      channelCount: channels.length,
      lastSaved: new Date().toLocaleString()
    };
  } catch {
    return { hasData: false, channelCount: 0 };
  }
};
